import { ArrowLeftRight, Check } from "lucide-react";
import { formatSiret } from "@utils/formatting";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@components/ui/sidebar";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { ROLES, ROLES_MAP } from "@user-management-context/shared/domain/types/enums/roles";
import { setActiveRole } from "@user-management-context/write/application/use-cases/active-role/set-active-role";
import { useSession } from "@user-management-context/primary/hooks/use-session";
import { writeActiveRoleToStorage } from "@shared-kernel/core/active-role/active-role";
import { resolveLandingUrl } from "@user-management-context/shared/domain/landing-url";
import { ROLE_META } from "@user-management-context/shared/onboarding/role-meta";
import { cn } from "@shared-kernel/primary/shared/shadcn/utils/utils";
import { selectAccountRolesRetrieval } from "@user-management-context/read/application/use-cases/account-roles/selectors/account-roles-selectors";
import { STRUCTURE_TYPE_LABELS } from "@user-management-context/read/domain/types/admin/teacher";
import type { AccountRoleVM } from "@shared-kernel/application/ports/shared/shared-repository";

const SWITCHER_LABEL = "Je suis un(e)";
const MISSING_SIRET_LABEL = "SIRET non renseigné";

const formatStructureLine = (structure: Pick<AccountRoleVM, "structureType" | "entityName">) => {
  const parts: string[] = [];
  if (structure.structureType) parts.push(STRUCTURE_TYPE_LABELS[structure.structureType]);
  if (structure.entityName) parts.push(structure.entityName);
  return parts.length > 0 ? parts.join(" · ") : null;
};

type RolePalette = {
  chip: string;
  ring: string;
  glow: string;
  marker: string;
};

const ROLE_PALETTE: Record<ROLES, RolePalette> = {
  [ROLES.STUDENT]: {
    chip: "bg-gradient-to-br from-emerald-50 to-emerald-100 text-emerald-700 dark:from-emerald-500/15 dark:to-emerald-400/5 dark:text-emerald-300",
    ring: "ring-emerald-200/70 dark:ring-emerald-400/20",
    glow: "shadow-[0_0_0_1px_rgba(16,185,129,0.18)]",
    marker: "bg-emerald-500",
  },
  [ROLES.TEACHER]: {
    chip: "bg-gradient-to-br from-amber-50 to-orange-100 text-amber-700 dark:from-amber-500/15 dark:to-orange-400/5 dark:text-amber-300",
    ring: "ring-amber-200/70 dark:ring-amber-400/20",
    glow: "shadow-[0_0_0_1px_rgba(245,158,11,0.20)]",
    marker: "bg-amber-500",
  },
  [ROLES.PROVIDER]: {
    chip: "bg-gradient-to-br from-indigo-50 to-violet-100 text-indigo-700 dark:from-indigo-500/15 dark:to-violet-400/5 dark:text-indigo-300",
    ring: "ring-indigo-200/70 dark:ring-indigo-400/20",
    glow: "shadow-[0_0_0_1px_rgba(99,102,241,0.20)]",
    marker: "bg-indigo-500",
  },
};

export function RoleSwitcher() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user, activeRole } = useSession();
  const { state, isMobile, setOpenMobile } = useSidebar();
  const isCollapsed = state === "collapsed" && !isMobile;
  const { data: accountRoles } = useAppSelector(selectAccountRolesRetrieval);

  if (!user) return null;

  const onboardedRoles = accountRoles.filter(r => r.status === "onboarded");
  const firstRole = onboardedRoles[0];
  if (!firstRole || onboardedRoles.length <= 1) return null;
  if (user.isAdmin && !activeRole) return null;

  const currentRole = activeRole ?? firstRole.role;
  const currentLabel = ROLES_MAP[currentRole];
  const currentMeta = ROLE_META[currentRole];
  const currentPalette = ROLE_PALETTE[currentRole];
  const CurrentIcon = currentMeta.Icon;

  const getStructureInfo = (role: ROLES) => {
    if (role === ROLES.STUDENT) return null;
    return accountRoles.find(r => r.role === role) ?? null;
  };

  const currentStructure = getStructureInfo(currentRole);
  const currentStructureLine = currentStructure ? formatStructureLine(currentStructure) : null;
  const collapsedTooltip = [
    `${SWITCHER_LABEL} · ${currentLabel}`,
    currentStructureLine,
    currentStructure ? `SIRET : ${currentStructure.siret ? formatSiret(currentStructure.siret) : MISSING_SIRET_LABEL}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const handleChange = (role: ROLES) => {
    if (role === activeRole) return;
    dispatch(setActiveRole(role));
    writeActiveRoleToStorage(role);
    if (isMobile) setOpenMobile(false);
    const target = resolveLandingUrl({ activeRole: role, isAdmin: user.isAdmin });
    if (target) navigate(target);
  };

  return (
    <SidebarMenu className="px-2 pb-3 pt-2">
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              tooltip={isCollapsed ? collapsedTooltip : undefined}
              className={cn(
                "group/role h-auto items-center gap-3 rounded-xl border border-sidebar-border/60 bg-sidebar-background/40 px-2.5 py-2.5 backdrop-blur-sm transition-all duration-300",
                "hover:border-sidebar-border hover:bg-sidebar-accent/60 hover:shadow-sm",
                "data-[state=open]:border-sidebar-border data-[state=open]:bg-sidebar-accent data-[state=open]:shadow-sm",
              )}
            >
              <span
                className={cn(
                  "flex size-9 shrink-0 items-center justify-center rounded-lg ring-1 transition-transform duration-300 group-hover/role:scale-[1.03]",
                  currentPalette.chip,
                  currentPalette.ring,
                )}
              >
                <CurrentIcon className="size-[18px]" />
              </span>
              <div className="grid flex-1 text-left leading-tight">
                <span className="truncate text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">{SWITCHER_LABEL}</span>
                <span className="truncate text-sm font-semibold text-sidebar-foreground">{currentLabel}</span>
                {currentStructureLine && <span className="truncate text-xs text-muted-foreground">{currentStructureLine}</span>}
              </div>
              <ArrowLeftRight
                className="ml-auto size-3.5 shrink-0 text-muted-foreground/70 transition-all duration-300 group-hover/role:translate-x-0.5 group-hover/role:text-foreground"
                aria-hidden
              />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-[280px] overflow-hidden rounded-xl p-1.5"
            side={isMobile ? "bottom" : "right"}
            align="start"
            sideOffset={8}
          >
            <DropdownMenuLabel className="px-2 pb-1.5 pt-1 text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              Changer de rôle
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="mb-1" />
            {onboardedRoles.map(({ role }) => {
              const meta = ROLE_META[role];
              const palette = ROLE_PALETTE[role];
              const RoleIcon = meta.Icon;
              const isActive = role === currentRole;
              const structure = getStructureInfo(role);
              const structureLine = structure ? formatStructureLine(structure) : null;
              return (
                <DropdownMenuItem
                  key={role}
                  onClick={() => handleChange(role)}
                  className={cn(
                    "group/item relative flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 transition-colors",
                    "focus:bg-accent/70",
                    isActive && "bg-accent/40",
                  )}
                >
                  <span
                    className={cn(
                      "absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-r-full transition-all duration-300",
                      isActive ? palette.marker : "bg-transparent group-hover/item:bg-border",
                    )}
                    aria-hidden
                  />
                  <span
                    className={cn(
                      "flex size-9 shrink-0 items-center justify-center rounded-lg ring-1 transition-transform duration-300 group-hover/item:scale-[1.03]",
                      palette.chip,
                      palette.ring,
                    )}
                  >
                    <RoleIcon className="size-[18px]" />
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col leading-tight">
                    <span className="truncate text-sm font-semibold text-foreground">{ROLES_MAP[role]}</span>
                    {structureLine && <span className="truncate text-xs text-muted-foreground">{structureLine}</span>}
                    {structure && (
                      <span
                        className={cn(
                          "truncate text-[11px]",
                          structure.siret ? "text-muted-foreground" : "italic text-muted-foreground/70",
                        )}
                      >
                        {structure.siret ? `SIRET : ${formatSiret(structure.siret)}` : MISSING_SIRET_LABEL}
                      </span>
                    )}
                  </div>
                  {isActive && (
                    <span
                      className={cn("flex size-5 shrink-0 items-center justify-center rounded-full text-white", palette.marker)}
                      aria-label="Rôle actif"
                    >
                      <Check className="size-3" strokeWidth={3} />
                    </span>
                  )}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
